import { Button, Chip } from '@material-tailwind/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Order } from '../../types';
import { SocketRPC, socketEmit } from '../lib/socket';
import { useOrderHistory, useOrders } from '../store/orders';
import useUser from '../store/user';

interface Props {
	orderKey: string | undefined | null;
}

const OrderInfoChip = ({ label, value }: { label: string; value: string }) => {
	return (
		<div className="bg-blue-600 rounded-lg text-white flex flex-col justify-left px-2 py-1">
			<div className="italic text-[0.6rem] font-bold">{`${label}:`}</div>
			<div className="text-xs">{value}</div>
		</div>
	);
};

export const ChatroomHeader = ({ orderKey }: Props) => {
	const { orders } = useOrders.getState();
	const navigate = useNavigate();
	const { orders: orderHistory, setOrders: setOrderHistory } = useOrderHistory.getState();
	const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
	const { user } = useUser();
	const { producerKey, orderSourceKey, partnerKey } = user || {};

	const fetchAndSetOrder = async () => {
		return socketEmit(SocketRPC.GetHistoricalOrder, {
			user,
			partnerKey,
			orderSourceKey,
			producerKey,
			orderKey,
		})
			.then((res) => {
				if (!res.success || !res.order) {
					throw new Error(res.error);
				}

				const fetchedOrder = res.orders;

				setOrderHistory({
					...orders,
					[fetchedOrder.orderKey]: fetchedOrder,
				});
				setCurrentOrder(fetchedOrder);
			})
			.catch((err) => {
				toast.error(`Error searching archived orders. Please try again`);
				console.log(`Could not get order`, err);
			});
	};

	useEffect(() => {
		if (orderKey) {
			const order = orders[orderKey];
			if (order) {
				setCurrentOrder(order);
			} else {
				fetchAndSetOrder();
			}
		}
	}, []);

	if (user?.isAnonymous) {
		return null;
	}
	return (
		<>
			<div className="py-3 px-8 flex gap-2 justify-end align-middle h-16">
				{currentOrder && (
					<>
						<OrderInfoChip label="External ID" value={currentOrder!.externalId} />
						<OrderInfoChip label="Consumer" value={currentOrder!.consumer.name} />
						<OrderInfoChip label="Producer" value={currentOrder!.producer.name} />
						<OrderInfoChip label="Source" value={currentOrder!.source.label} />
					</>
				)}
				{orderKey ? (
					<Button
						type="button"
						size="sm"
						className="h-full bg-green-600 hover:bg-green-400 text-white flex gap-3 justify-center leading-6"
						onClick={() =>
							orders[orderKey]
								? navigate(`/active-orders/${orderKey}`)
								: navigate(`/order-history/${orderKey}`)
						}
					>{`Order Details`}</Button>
				) : (
					<Chip color="blue" value="General Chat (no associated order)" />
				)}
			</div>
		</>
	);
};
